import { ChangeDetectionStrategy, Component, input } from '@angular/core'
import { InsuredBaseForm } from '@features/insured/components/insured-base-form.directive'
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AlbertOverrideWrapper } from '@shared/components/albert-override-wrapper/albert-override-wrapper'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { ReinsuranceFormComponent } from '@features/insured/components/coverages/reinsurance-form/reinsurance-form.component'
import { AlbertLimitComponent } from '@shared/components/albert-limit/albert-limit.component'
import { AlbertPeriodComponent } from '@shared/components/albert-period/albert-period.component'
import { BaseSubCoverageForm } from '@features/insured/models/coverages/base-coverage.model'
import { hasActualValue, isAlbertValueObjectForm } from '@core/utils/form-utils'

const LIMIT_FIELDS = new Set([
  'limit',
  'aggregateLimit',
  'personalInjurySublimit',
  'groundingLiabilitySublimit',
  'confiscationSublimit',
  'sparesDeductible'
])

@Component({
  selector: 'albert-standard-sub-coverage-form',
  imports: [
    ReactiveFormsModule,
    AlbertOverrideWrapper,
    AlbertInputComponent,
    ReinsuranceFormComponent,
    AlbertLimitComponent,
    AlbertPeriodComponent
  ],
  templateUrl: './base-sub-coverage-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class BaseSubCoverageFormComponent extends InsuredBaseForm<BaseSubCoverageForm> {
  public override readonly form = input.required<FormGroup>()

  public get controls(): BaseSubCoverageForm {
    return this.form().controls as unknown as BaseSubCoverageForm
  }

  public override isFieldVisible(field: string): boolean {
    if (this.overrideService.showAll() || this.manuallyExpandedFields().has(field)) {
      return true
    }

    const control = this.getFormGroup().get(field)
    if (!control) return false

    if (isAlbertValueObjectForm(control)) {
      return hasActualValue(control.controls.localValue.value)
    }

    if (LIMIT_FIELDS.has(field)) {
      const group = control as FormGroup | undefined
      const valueControl = group?.get('value')
      const currencyControl = group?.get('currency')

      return (isAlbertValueObjectForm(valueControl) && hasActualValue(valueControl.controls.localValue.value)) ||
        (isAlbertValueObjectForm(currencyControl) && hasActualValue(currencyControl.controls.localValue.value) &&
          currencyControl.controls.localValue.value !== 'EUR')
    }

    return false
  }

  public override toggleField(field: string): void {
    if (!this.canEdit()) return

    const control = this.getFormGroup().get(field)
    if (!control) return

    const isVisible = this.isFieldVisible(field)

    if (isAlbertValueObjectForm(control)) {
      const newValue = isVisible ? null : ''
      control.controls.localValue.setValue(newValue)
      this.toggleExpansion(field, !isVisible)
      return
    }

    if (LIMIT_FIELDS.has(field)) {
      const group = control as FormGroup | undefined
      if (!group) return

      this.resetAlbertLimitControl(group.get('value'))
      this.resetAlbertLimitControl(group.get('currency'))
      this.toggleExpansion(field, !isVisible)
    }
  }

  protected override getFormGroup(): FormGroup<BaseSubCoverageForm> {
    return this.form() as FormGroup<BaseSubCoverageForm>
  }

  private toggleExpansion(field: string, expand: boolean): void {
    this.manuallyExpandedFields.update(prev => {
      const next = new Set(prev)
      if (expand) {
        next.add(field)
      } else {
        next.delete(field)
      }
      return next
    })
  }

  private resetAlbertLimitControl(control: AbstractControl | null): void {
    if (isAlbertValueObjectForm(control)) {
      control.controls.localValue.setValue(null)
    }
  }

}
